import React, { useMemo, useState, useEffect } from 'react';
import { Briefcase, Search as SearchIcon, AlertTriangle } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import EntityLink from '../components/shared/EntityLink';
import { useOfertasPipeline } from '../hooks/useOfertasPipeline';
import { colaboradores } from '../utils/stageHelpers';
import OfertaDetailModal from '../components/OfertaDetailModal';
import { supabase } from '../supabaseClient';

const RESP_OPTIONS = [{ value: '', label: '—' }, ...colaboradores.map((c) => ({ value: c.id, label: c.nome }))];

function StatusBadge({ status }) {
  if (!status) return <span className="text-slate-400 text-[10px]">—</span>;
  const isDiscarded = status.startsWith('9.');
  const isDone = status.startsWith('8.');
  const isPropose = status.startsWith('4.') || status.startsWith('5.') || status.startsWith('6.') || status.startsWith('7.');
  const tone = isDiscarded
    ? 'bg-slate-200 text-slate-500 line-through'
    : isDone
      ? 'bg-emerald-100 text-emerald-800'
      : isPropose
        ? 'bg-blue-100 text-blue-800'
        : 'bg-amber-50 text-amber-800';
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${tone}`}>{status}</span>;
}

function fmtDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function isFupExpired(v) {
  if (!v) return false;
  const d = new Date(v);
  if (isNaN(d.getTime())) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return d < today;
}

export default function OfertasPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // prefix: '' | '4.' | '9.' etc
  const [responsavelFilter, setResponsavelFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [openOfertaId, setOpenOfertaId] = useState(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) setSortDir((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const { data, loading, error, refetch } = useOfertasPipeline({ showDiscarded });

  // T6: negociadores por oferta (v_ofertas_pipeline não expõe esses joins)
  const [negociadoresMap, setNegociadoresMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('ofertas')
      .select('id, contato_negociador:contatos!ofertas_contato_negociador_id_fkey(id, first_name, last_name, title, eh_decisor)')
      .not('contato_negociador_id', 'is', null)
      .then(({ data: rows }) => {
        if (cancelled || !rows) return;
        const map = {};
        rows.forEach((r) => { if (r.contato_negociador) map[r.id] = r.contato_negociador; });
        setNegociadoresMap(map);
      });
    return () => { cancelled = true; };
  }, [data]);

  const areas = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    data.forEach((r) => { if (r.area_id && !map.has(r.area_id)) map.set(r.area_id, r.area_nome); });
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome })).sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.filter((r) => {
      if (statusFilter && !(r.status || '').startsWith(statusFilter)) return false;
      if (responsavelFilter && r.responsavel_id !== responsavelFilter) return false;
      if (areaFilter && r.area_id !== areaFilter) return false;
      if (needle) {
        const bag = [r.nome, r.company_name, r.area_nome, r.status].filter(Boolean).join(' ').toLowerCase();
        if (!bag.includes(needle)) return false;
      }
      return true;
    });
  }, [data, search, statusFilter, responsavelFilter, areaFilter]);

  const sorted = useMemo(() => {
    if (!sortField || !filtered) return filtered;
    return [...filtered].sort((a, b) => {
      let va; let vb;
      switch (sortField) {
        case 'empresa': va = a.company_name || ''; vb = b.company_name || ''; break;
        case 'negociador': {
          const na = negociadoresMap[a.id]; const nb = negociadoresMap[b.id];
          va = na ? `${na.first_name || ''} ${na.last_name || ''}`.trim() : '';
          vb = nb ? `${nb.first_name || ''} ${nb.last_name || ''}`.trim() : '';
          break;
        }
        case 'oferta': va = a.nome || ''; vb = b.nome || ''; break;
        case 'status': va = a.status || ''; vb = b.status || ''; break;
        case 'area': va = a.area_nome || ''; vb = b.area_nome || ''; break;
        case 'fup': va = a.data_fup || ''; vb = b.data_fup || ''; break;
        case 'emails': va = a.email_count || 0; vb = b.email_count || 0; break;
        case 'propostas': va = a.proposta_count || 0; vb = b.proposta_count || 0; break;
        case 'resp': va = a.responsavel_id || ''; vb = b.responsavel_id || ''; break;
        default: return 0;
      }
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [filtered, sortField, sortDir, negociadoresMap]);

  const SortHeader = ({ field, label, align = 'text-left', minW = 80 }) => (
    <th
      className={`px-3 py-2 ${align} text-[10px] cursor-pointer hover:text-blue-700 select-none overflow-hidden resize-x`}
      style={{ minWidth: `${minW}px` }}
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field && (sortDir === 'asc' ? '▲' : '▼')}
    </th>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader title="Ofertas" subtitle="Pipeline global" icon={Briefcase} />
      <main className="w-full px-4 lg:px-6 2xl:px-8 py-4 lg:py-6 space-y-3">
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <SearchIcon size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar oferta, empresa, área..."
              className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-blue-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-2 border border-slate-200 rounded text-xs bg-white"
          >
            <option value="">Todos os status</option>
            <option value="1.">Ativas (1.x)</option>
            <option value="2.">Prospectar (2.x)</option>
            <option value="3.">Contato (3.x)</option>
            <option value="4.">Proposta (4.x)</option>
            <option value="5.">Negociação (5.x)</option>
            <option value="8.">Fechadas (8.x)</option>
            <option value="9.">Descartadas (9.x)</option>
          </select>
          <select
            value={responsavelFilter}
            onChange={(e) => setResponsavelFilter(e.target.value)}
            className="px-2 py-2 border border-slate-200 rounded text-xs bg-white"
          >
            {RESP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label || '— responsável —'}</option>)}
          </select>
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            className="px-2 py-2 border border-slate-200 rounded text-xs bg-white max-w-[200px]"
          >
            <option value="">Todas as áreas</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
          </select>
          <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showDiscarded} onChange={(e) => setShowDiscarded(e.target.checked)} />
            Mostrar descartadas
          </label>
          <div className="text-[11px] text-slate-500 ml-auto">
            {loading ? '...' : `${filtered.length}${data ? ` / ${data.length}` : ''}`}
          </div>
        </div>

        {loading && <LoadingSpinner label="Carregando pipeline..." />}
        {!loading && error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && filtered.length === 0 && (
          <EmptyState
            message={search || statusFilter || responsavelFilter || areaFilter ? 'Nenhuma oferta bate com os filtros' : 'Nenhuma oferta no pipeline'}
            icon={Briefcase}
          />
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg">
            <div>
              <table className="w-full text-xs min-w-[1400px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0 z-10">
                <tr>
                  <SortHeader field="empresa" label="Empresa" minW={150} />
                  <SortHeader field="negociador" label="Negociador" minW={130} />
                  <SortHeader field="oferta" label="Oferta" minW={200} />
                  <SortHeader field="status" label="Status" minW={120} />
                  <SortHeader field="area" label="Área" minW={180} />
                  <SortHeader field="fup" label="FUP" minW={80} />
                  <SortHeader field="emails" label="Emails" align="text-right" minW={60} />
                  <SortHeader field="propostas" label="Propostas" align="text-right" minW={70} />
                  <SortHeader field="resp" label="Resp." minW={80} />
                  <th className="px-3 py-2 text-center text-[10px] overflow-hidden resize-x" style={{ minWidth: '50px' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const fupExpired = isFupExpired(r.data_fup);
                  const isDiscarded = (r.status || '').startsWith('9.');
                  return (
                    <tr
                      key={r.id}
                      className={`border-t border-slate-100 hover:bg-blue-50 ${isDiscarded ? 'opacity-60' : ''}`}
                    >
                      <td className="px-3 py-1.5 max-w-[180px]">
                        {r.empresa_id ? (
                          <EntityLink entityType="empresa" entityId={r.empresa_id} label={r.company_name || '—'} showIcon={false} />
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5 max-w-[150px]">
                        {negociadoresMap[r.id] ? (
                          <div className="flex items-center gap-1">
                            <EntityLink
                              entityType="contato"
                              entityId={negociadoresMap[r.id].id}
                              label={`${negociadoresMap[r.id].first_name || ''} ${negociadoresMap[r.id].last_name || ''}`.trim() || '—'}
                              showIcon={false}
                            />
                            {negociadoresMap[r.id].eh_decisor && <span className="text-[9px]" title="Decisor">⭐</span>}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5 max-w-[250px]">
                        <button
                          onClick={() => setOpenOfertaId(r.id)}
                          className="text-left text-orange-700 hover:underline truncate block w-full"
                          title={r.nome || ''}
                        >
                          {r.nome || <span className="text-slate-400">—</span>}
                        </button>
                      </td>
                      <td className="px-3 py-1.5"><StatusBadge status={r.status} /></td>
                      <td className="px-3 py-1.5 max-w-[150px] text-slate-600 truncate" title={r.area_nome || ''}>{r.area_nome || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-1.5 w-[90px] whitespace-nowrap">
                        {r.data_fup ? (
                          <span className={`inline-flex items-center gap-1 text-[11px] ${fupExpired ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>
                            {fupExpired && <AlertTriangle size={10} />}
                            {fmtDate(r.data_fup)}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{r.email_count ?? 0}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{r.proposta_count ?? 0}</td>
                      <td className="px-3 py-1.5 text-slate-600 capitalize whitespace-nowrap">{r.responsavel_id || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-1.5 text-center">
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`Excluir oferta "${r.nome}"?\n\nIsto removerá permanentemente a oferta e todas as propostas vinculadas.`)) return;
                            const { error: delErr } = await supabase.from('ofertas').delete().eq('id', r.id);
                            if (delErr) { window.alert(`Erro ao excluir: ${delErr.message}`); return; }
                            refetch();
                          }}
                          className="text-red-400 hover:text-red-600 text-[12px]"
                          title="Excluir oferta"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </main>
      {openOfertaId && (
        <OfertaDetailModal
          ofertaId={openOfertaId}
          onClose={() => { setOpenOfertaId(null); refetch(); }}
        />
      )}
    </div>
  );
}
